/**
 * Fiche étudiant 360° : calendrier de présence + EDT graphique + paiements + résultats + conduite.
 * Affichable en modal depuis le dashboard absences (clic matricule) ou la page étudiant.
 */
import React, { useMemo, useState } from 'react';
import {
  X, User, Calendar, Clock, Wallet, GraduationCap, Heart,
  ChevronLeft, ChevronRight, School, MapPin, Phone, Mail,
  BookOpen, FileText, TrendingUp,
} from 'lucide-react';
import { useFicheEtudiantQuery } from '../../services/vieScolaireApi';

interface Props {
  matricule: string;
  onClose: () => void;
}

const STATUT_COULEUR: Record<string, string> = {
  present: 'bg-green-500',
  absent: 'bg-red-500',
  retard: 'bg-amber-500',
  justifie: 'bg-blue-400',
  incident: 'bg-orange-500',
};
const STATUT_LABEL: Record<string, string> = {
  present: 'Présent',
  absent: 'Absent',
  retard: 'Retard',
  justifie: 'Justifié',
  incident: 'Incident',
};
const JOURS_FR = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
const JOURS_COURT = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];

// Palette pastel pour distinguer les matières dans l'EDT graphique
const PALETTE = [
  'bg-sky-100 text-sky-800 border-sky-300 dark:bg-sky-900/40 dark:text-sky-200 dark:border-sky-700',
  'bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-900/40 dark:text-emerald-200 dark:border-emerald-700',
  'bg-violet-100 text-violet-800 border-violet-300 dark:bg-violet-900/40 dark:text-violet-200 dark:border-violet-700',
  'bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-900/40 dark:text-amber-200 dark:border-amber-700',
  'bg-rose-100 text-rose-800 border-rose-300 dark:bg-rose-900/40 dark:text-rose-200 dark:border-rose-700',
  'bg-cyan-100 text-cyan-800 border-cyan-300 dark:bg-cyan-900/40 dark:text-cyan-200 dark:border-cyan-700',
  'bg-lime-100 text-lime-800 border-lime-300 dark:bg-lime-900/40 dark:text-lime-200 dark:border-lime-700',
  'bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300 dark:bg-fuchsia-900/40 dark:text-fuchsia-200 dark:border-fuchsia-700',
];

function ymd(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const j = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${j}`;
}

function frDate(s: string): string {
  return new Date(s).toLocaleDateString('fr-FR', {
    weekday: 'long', day: '2-digit', month: 'long',
  });
}

function timeToMin(t: string): number {
  if (!t) return 0;
  const [h, m] = t.split(':').map(Number);
  return h * 60 + (m || 0);
}

function colorForMatiere(libelle: string): string {
  let h = 0;
  for (let i = 0; i < libelle.length; i++) h = (h * 31 + libelle.charCodeAt(i)) & 0xffff;
  return PALETTE[h % PALETTE.length];
}

export default function FicheEtudiantModal({ matricule, onClose }: Props) {
  const today = new Date();
  const ya60 = new Date(today.getTime() - 60 * 86_400_000);
  const [depuis] = useState(ymd(ya60));
  const [jusqua] = useState(ymd(today));
  const [moisCourant, setMoisCourant] = useState(
    new Date(today.getFullYear(), today.getMonth(), 1)
  );
  const [jourSelectionne, setJourSelectionne] = useState<string | null>(null);

  const { data, isFetching, error } = useFicheEtudiantQuery({ matricule, depuis, jusqua });

  const parJourMap = useMemo(() => {
    const m: Record<string, any> = {};
    (data?.presences.par_jour || []).forEach((p: any) => { m[p.date] = p; });
    return m;
  }, [data]);

  const detailJour = jourSelectionne ? parJourMap[jourSelectionne] : null;

  const cellulesMois = useMemo(() => {
    const y = moisCourant.getFullYear();
    const m = moisCourant.getMonth();
    const premier = new Date(y, m, 1);
    const dernier = new Date(y, m + 1, 0);
    const decalage = (premier.getDay() + 6) % 7;
    const cells: Array<{ date: string; jour: number } | null> = [];
    for (let i = 0; i < decalage; i++) cells.push(null);
    for (let j = 1; j <= dernier.getDate(); j++) {
      cells.push({ date: ymd(new Date(y, m, j)), jour: j });
    }
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [moisCourant]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-2 md:p-4"
      onClick={onClose}>
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-7xl max-h-[95vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 md:px-5 py-2.5 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
          <div className="flex items-center gap-2">
            <User size={16} className="text-gray-700 dark:text-gray-300" />
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
              Fiche étudiant — <span className="font-mono">{matricule}</span>
            </h2>
          </div>
          <button onClick={onClose}
            className="p-1.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded text-gray-600 dark:text-gray-400">
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {isFetching && !data && (
            <div className="text-center text-gray-500 py-16 text-sm">Chargement de la fiche…</div>
          )}
          {!!error && (
            <div className="text-center text-red-500 py-16 text-sm">
              {(error as any)?.data?.detail || 'Erreur de chargement'}
            </div>
          )}
          {data && (
            <div className="p-3 md:p-5 space-y-3">
              {/* Identité + KPI sur la même ligne */}
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-3">
                <div className="lg:col-span-2">
                  <Identite ident={data.identite} />
                </div>
                <div className="lg:col-span-3">
                  <KpiBande
                    tauxPresence={data.presences.taux_presence}
                    totalSeances={data.presences.total_seances}
                    nbParStatut={data.presences.nb_par_statut}
                    conduite={data.conduite}
                    paiements={data.paiements.resume}
                  />
                </div>
              </div>

              {/* Calendrier compact + EDT graphique large */}
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-3">
                <div className="lg:col-span-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-xs font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-1.5">
                      <Calendar size={13} /> Présences
                    </h3>
                    <div className="flex items-center gap-0.5">
                      <button onClick={() => setMoisCourant(new Date(moisCourant.getFullYear(), moisCourant.getMonth() - 1, 1))}
                        className="p-0.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-gray-600">
                        <ChevronLeft size={12} />
                      </button>
                      <span className="text-[11px] font-medium text-gray-700 dark:text-gray-300 w-24 text-center capitalize">
                        {moisCourant.toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' })}
                      </span>
                      <button onClick={() => setMoisCourant(new Date(moisCourant.getFullYear(), moisCourant.getMonth() + 1, 1))}
                        className="p-0.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-gray-600">
                        <ChevronRight size={12} />
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-7 gap-0.5 mb-0.5">
                    {['L', 'M', 'M', 'J', 'V', 'S', 'D'].map((j, i) => (
                      <div key={i} className="text-center text-[9px] uppercase text-gray-400 font-semibold">
                        {j}
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-7 gap-0.5">
                    {cellulesMois.map((c, i) => {
                      if (!c) return <div key={i} className="h-7" />;
                      const p = parJourMap[c.date];
                      const isToday = c.date === ymd(today);
                      const isSel = c.date === jourSelectionne;
                      const couleur = p ? STATUT_COULEUR[p.statut_dominant] : '';
                      return (
                        <button key={i}
                          onClick={() => p && setJourSelectionne(c.date)}
                          disabled={!p}
                          className={`h-7 rounded text-[10px] font-medium relative transition-all flex items-center justify-center
                            ${p ? 'cursor-pointer hover:ring-1 hover:ring-blue-400' : 'cursor-default'}
                            ${isSel ? 'ring-2 ring-blue-500 ring-offset-1 ring-offset-white dark:ring-offset-gray-800' : ''}
                            ${isToday && !isSel ? 'ring-1 ring-blue-400' : ''}
                            ${p ? 'text-white ' + couleur : 'bg-gray-50 dark:bg-gray-700/30 text-gray-400'}`}
                          title={p ? `${STATUT_LABEL[p.statut_dominant]} — ${p.nb_seances} séance(s)` : 'Pas de cours'}>
                          {c.jour}
                        </button>
                      );
                    })}
                  </div>

                  {/* Légende compacte */}
                  <div className="flex flex-wrap gap-x-2 gap-y-1 mt-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                    {Object.entries(STATUT_COULEUR).map(([k, v]) => (
                      <div key={k} className="flex items-center gap-1 text-[9px] text-gray-500 dark:text-gray-400">
                        <span className={`inline-block w-2 h-2 rounded-sm ${v}`} />
                        {STATUT_LABEL[k]}
                      </div>
                    ))}
                  </div>

                  {/* Détail jour */}
                  {detailJour && (
                    <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                      <h4 className="text-[11px] font-semibold text-gray-700 dark:text-gray-300 mb-1.5 capitalize">
                        {frDate(detailJour.date)}
                      </h4>
                      <div className="space-y-1">
                        {detailJour.detail.map((d: any) => (
                          <div key={d.seance_id} className="flex items-start gap-1.5 p-1.5 bg-gray-50 dark:bg-gray-700/40 rounded text-[10px]">
                            <span className={`inline-block w-1.5 h-1.5 rounded-full mt-1 flex-shrink-0 ${STATUT_COULEUR[d.statut]}`} />
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-gray-900 dark:text-white truncate">
                                {d.heure_debut} · {d.libelle_matiere}
                              </div>
                              <div className="text-gray-500 dark:text-gray-400">
                                {STATUT_LABEL[d.statut]}
                                {d.statut === 'retard' && d.minutes_retard > 0 && ` (+${d.minutes_retard}min)`}
                                {d.justification_validee && ' ✓'}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="lg:col-span-3">
                  <EdtGrille edt={data.emploi_du_temps || []} />
                </div>
              </div>

              {/* Présences par mois + Participation examens */}
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-3">
                <div className="lg:col-span-3">
                  <PresencesMensuelles
                    parMois={data.presences_par_mois || []}
                    totaux={data.presences_totaux_annee}
                  />
                </div>
                <div className="lg:col-span-2">
                  <ParticipationExamens p={data.participation_examens} />
                </div>
              </div>

              {/* Notes devoirs + examens (avec toggle semestre impair/pair) */}
              <NotesBloc
                devoirs={data.notes_devoirs}
                examens={data.notes_examens}
              />

              {/* Paiements + Résultats + Conduite */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                <Paiements pmt={data.paiements} />
                <Resultats results={data.resultats} />
                <Conduite cond={data.conduite} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Identite({ ident }: { ident: any }) {
  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 flex gap-3 h-full">
      <div className="w-16 h-16 flex-shrink-0 bg-gray-100 dark:bg-gray-700 rounded overflow-hidden flex items-center justify-center">
        {ident.photo_url ? (
          <img src={ident.photo_url} alt={ident.nom} className="w-full h-full object-cover" />
        ) : (
          <User size={24} className="text-gray-400" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-bold text-gray-900 dark:text-white truncate">
          {ident.nom} {ident.prenoms}
        </div>
        <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">
          {ident.matricule}{ident.sexe && ` · ${ident.sexe === 'M' ? 'M' : 'F'}`}{ident.age != null && ` · ${ident.age} ans`}
        </div>
        <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-[10px]">
          {ident.classe && <Info Icon={School} value={ident.classe} />}
          {ident.option && <Info Icon={MapPin} value={ident.option} />}
          {ident.telephone && <Info Icon={Phone} value={ident.telephone} />}
          {ident.email && <Info Icon={Mail} value={ident.email} />}
        </div>
      </div>
    </div>
  );
}

function Info({ Icon, value }: { Icon: any; value: string }) {
  return (
    <div className="flex items-center gap-1 text-gray-600 dark:text-gray-400">
      <Icon size={10} className="text-gray-400" />
      <span className="text-gray-700 dark:text-gray-300 truncate">{value}</span>
    </div>
  );
}

function KpiBande({ tauxPresence, totalSeances, nbParStatut, conduite, paiements }: any) {
  const couleurTaux = tauxPresence >= 90 ? 'text-green-500' : tauxPresence >= 75 ? 'text-amber-500' : 'text-red-500';
  const items: Array<{ value: string; couleur: string; label: string; sous: string }> = [
    {
      value: `${tauxPresence.toFixed(1)}%`,
      couleur: couleurTaux,
      label: 'Présence',
      sous: `${totalSeances} séances`,
    },
    {
      value: String(nbParStatut.absent),
      couleur: 'text-red-500',
      label: 'Absences',
      sous: `${nbParStatut.justifie} just.`,
    },
    {
      value: conduite.niveau,
      couleur:
        conduite.niveau === 'Excellent' ? 'text-green-500' :
        conduite.niveau === 'Bon' ? 'text-blue-500' :
        conduite.niveau === 'Passable' ? 'text-amber-500' : 'text-red-500',
      label: 'Conduite',
      sous: `Score ${conduite.score}`,
    },
    {
      value: paiements.a_jour_global ? 'À jour' : 'Solde',
      couleur: paiements.a_jour_global ? 'text-green-500' : 'text-red-500',
      label: 'Paiements',
      sous: `${paiements.nb_a_jour}/${paiements.nb_periodes}`,
    },
  ];
  return (
    <div className="grid grid-cols-4 gap-2 h-full">
      {items.map((k, i) => (
        <div key={i} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-2.5 text-center flex flex-col justify-center">
          <div className={`text-lg font-bold ${k.couleur} leading-tight`}>{k.value}</div>
          <div className="text-[9px] uppercase text-gray-500 tracking-wide font-semibold mt-0.5">{k.label}</div>
          <div className="text-[9px] text-gray-400 dark:text-gray-500">{k.sous}</div>
        </div>
      ))}
    </div>
  );
}

function EdtGrille({ edt }: { edt: any[] }) {
  // Plage horaire dynamique alignée sur l'heure pleine
  const { slots, minStart, pxPerMin } = useMemo(() => {
    if (!edt.length) return { slots: [], minStart: 0, pxPerMin: 0 };
    let mn = 24 * 60, mx = 0;
    for (const e of edt) {
      const d = timeToMin(e.heure_debut);
      const f = timeToMin(e.heure_fin);
      if (d < mn) mn = d;
      if (f > mx) mx = f;
    }
    mn = Math.floor(mn / 60) * 60;
    mx = Math.ceil(mx / 60) * 60;
    const sl: number[] = [];
    for (let t = mn; t <= mx; t += 60) sl.push(t);
    return { slots: sl, minStart: mn, pxPerMin: 0.75 }; // 0.75 px/min → 45 px / heure
  }, [edt]);

  const totalHeight = slots.length ? (slots[slots.length - 1] - slots[0]) * pxPerMin : 0;
  const jourSemaine = (new Date().getDay() + 6) % 7;

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 h-full">
      <h3 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-1.5">
        <Clock size={13} /> Emploi du temps hebdomadaire
      </h3>

      {edt.length === 0 ? (
        <div className="text-center text-xs text-gray-500 py-6">Pas d'EDT enregistré pour cette classe</div>
      ) : (
        <div className="flex">
          <div className="w-8 flex-shrink-0 relative" style={{ height: totalHeight + 16 }}>
            <div className="h-4" />
            {slots.map((t, i) => (
              <div key={i} className="absolute left-0 right-0 text-[9px] text-gray-400 -translate-y-1.5"
                style={{ top: 16 + (t - minStart) * pxPerMin }}>
                {String(Math.floor(t / 60)).padStart(2, '0')}h
              </div>
            ))}
          </div>

          <div className="flex-1 grid grid-cols-6 gap-0.5">
            {JOURS_FR.map((jour, idx) => {
              const k = jour.toLowerCase();
              const evts = edt.filter((e) => (e.jour || '').toLowerCase() === k);
              const isAujourdhui = idx === jourSemaine;
              return (
                <div key={k} className={`relative border-l border-gray-100 dark:border-gray-700 ${
                  isAujourdhui ? 'bg-blue-50/40 dark:bg-blue-900/10' : ''
                }`}>
                  <div className={`h-4 text-center text-[9px] font-semibold ${
                    isAujourdhui ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500'
                  }`}>
                    {JOURS_COURT[idx]}
                  </div>
                  <div className="relative" style={{ height: totalHeight }}>
                    {slots.map((t, i) => i < slots.length - 1 && (
                      <div key={i} className="absolute left-0 right-0 border-t border-gray-100 dark:border-gray-700/50"
                        style={{ top: (t - minStart) * pxPerMin }} />
                    ))}
                    {evts.map((e) => {
                      const top = (timeToMin(e.heure_debut) - minStart) * pxPerMin;
                      const h = Math.max(30, (timeToMin(e.heure_fin) - timeToMin(e.heure_debut)) * pxPerMin);
                      const color = colorForMatiere(e.libelle_matiere);
                      return (
                        <div key={e.id}
                          className={`absolute left-0.5 right-0.5 rounded border px-1 py-0.5 overflow-hidden text-[9px] leading-tight ${color}`}
                          style={{ top, height: h - 1 }}
                          title={`${e.heure_debut}–${e.heure_fin}\n${e.libelle_matiere}\n${e.formateur_nom || ''}\n${e.salle || ''}`}>
                          <div className="font-semibold truncate">{e.libelle_matiere || e.code_matiere}</div>
                          {h >= 36 && e.formateur_nom && (
                            <div className="opacity-80 truncate text-[8px]">{e.formateur_nom}</div>
                          )}
                          {h >= 52 && e.salle && (
                            <div className="opacity-70 truncate text-[8px]">{e.salle} · {e.heure_debut}</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function Paiements({ pmt }: { pmt: any }) {
  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
      <h3 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-1.5">
        <Wallet size={13} /> État financier
      </h3>
      {pmt.detail.length === 0 ? (
        <div className="text-center text-xs text-gray-500 py-6">Pas de données</div>
      ) : (
        <>
          <div className="space-y-1">
            {pmt.detail.map((p: any) => (
              <div key={p.periode} className="flex items-center justify-between text-[11px] py-1 px-2 rounded bg-gray-50 dark:bg-gray-700/50">
                <span className="text-gray-700 dark:text-gray-300 truncate">{p.periode_label}</span>
                <span className={`px-1.5 py-0.5 rounded font-bold text-[9px] flex-shrink-0 ml-2 ${
                  p.est_a_jour
                    ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                    : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                }`}>
                  {p.est_a_jour ? 'À jour' : `${Number(p.solde_du).toLocaleString('fr-FR')} F`}
                </span>
              </div>
            ))}
          </div>
          {Number(pmt.resume.solde_total) > 0 && (
            <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between text-[11px]">
              <span className="font-semibold text-gray-700 dark:text-gray-300">Solde total</span>
              <span className="font-bold text-red-500">
                {Number(pmt.resume.solde_total).toLocaleString('fr-FR')} F
              </span>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Resultats({ results }: { results: any[] }) {
  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
      <h3 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-1.5">
        <GraduationCap size={13} /> Résultats récents
      </h3>
      {results.length === 0 ? (
        <div className="text-center text-xs text-gray-500 py-6">Aucun résultat</div>
      ) : (
        <div className="space-y-1">
          {results.slice(0, 5).map((r, i) => (
            <div key={i} className="flex items-center justify-between text-[11px] py-1 px-2 rounded bg-gray-50 dark:bg-gray-700/50">
              <div className="min-w-0 flex-1">
                <div className="font-medium text-gray-900 dark:text-white">
                  S{r.semestre} <span className="text-gray-500 text-[10px]">{r.annee_academique}</span>
                </div>
                <div className="text-[9px] text-gray-500 truncate">
                  {r.mention || '—'}
                  {r.rang && r.effectif_classe && ` · ${r.rang}/${r.effectif_classe}`}
                </div>
              </div>
              <div className="text-right ml-2">
                <div className={`text-sm font-bold ${
                  Number(r.moyenne) >= 10 ? 'text-green-500' : 'text-red-500'
                }`}>
                  {r.moyenne ? Number(r.moyenne).toFixed(2) : '—'}
                </div>
                <div className="text-[9px] text-gray-500">
                  {r.credits_obtenus}/{r.credits_requis}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PresencesMensuelles({ parMois, totaux }: { parMois: any[]; totaux: any }) {
  const max = Math.max(1, ...parMois.map((m) => m.total));
  const labelMois = (ym: string) => {
    const [y, mm] = ym.split('-');
    const d = new Date(Number(y), Number(mm) - 1, 1);
    return d.toLocaleDateString('fr-FR', { month: 'short' });
  };
  const tauxAn = totaux && totaux.total
    ? Math.round(((totaux.present + totaux.retard) * 100) / totaux.total)
    : 0;
  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 h-full">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-1.5">
          <TrendingUp size={13} /> Présences par mois (12 derniers mois)
        </h3>
        {totaux && (
          <div className="flex gap-3 text-[10px]">
            <span className="text-gray-500">Total : <span className="font-bold text-gray-900 dark:text-white">{totaux.total}</span></span>
            <span className="text-green-600">Présents : <span className="font-bold">{totaux.present}</span></span>
            <span className="text-red-500">Absents : <span className="font-bold">{totaux.absent}</span></span>
            <span className="text-gray-500">Taux : <span className="font-bold text-gray-900 dark:text-white">{tauxAn}%</span></span>
          </div>
        )}
      </div>
      {parMois.length === 0 ? (
        <div className="text-center text-xs text-gray-500 py-8">Pas de présences enregistrées</div>
      ) : (
        <div className="flex items-end gap-1 h-32 px-2 pt-1 border-t border-gray-100 dark:border-gray-700">
          {parMois.map((m) => {
            const hPres = (m.present / max) * 100;
            const hRet = (m.retard / max) * 100;
            const hAbs = (m.absent / max) * 100;
            const hJust = (m.justifie / max) * 100;
            return (
              <div key={m.mois} className="flex-1 flex flex-col items-center gap-1 min-w-0">
                <div className="flex-1 w-full flex flex-col-reverse justify-start" title={`${labelMois(m.mois)} : ${m.total} pointages (${m.present} pres, ${m.absent} abs, ${m.retard} ret, ${m.justifie} just)`}>
                  <div style={{ height: `${hPres}%` }} className="bg-green-500 w-full" />
                  <div style={{ height: `${hRet}%` }} className="bg-amber-500 w-full" />
                  <div style={{ height: `${hJust}%` }} className="bg-blue-400 w-full" />
                  <div style={{ height: `${hAbs}%` }} className="bg-red-500 w-full" />
                </div>
                <div className="text-[9px] text-gray-500 uppercase truncate w-full text-center">{labelMois(m.mois)}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ParticipationExamens({ p }: { p: any }) {
  const sessions: Array<{ k: 'NORMAL' | 'RATTRAPAGE'; label: string; chipBg: string; chipText: string; titre: string }> = [
    {
      k: 'NORMAL', label: 'Session ordinaire',
      titre: 'text-blue-600 dark:text-blue-400',
      chipBg: 'bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800',
      chipText: 'text-blue-800 dark:text-blue-200',
    },
    {
      k: 'RATTRAPAGE', label: 'Session rattrapage',
      titre: 'text-purple-600 dark:text-purple-400',
      chipBg: 'bg-purple-50 dark:bg-purple-900/30 border-purple-200 dark:border-purple-800',
      chipText: 'text-purple-800 dark:text-purple-200',
    },
  ];
  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 h-full">
      <h3 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-1.5">
        <FileText size={13} /> Matières où l'étudiant a composé
      </h3>
      <div className="space-y-3">
        {sessions.map((s) => {
          const blocs = (p?.[s.k] || []) as any[];
          const total = blocs.reduce((acc, b) => acc + b.nb_matieres, 0);
          return (
            <div key={s.k}>
              <div className="flex items-center justify-between mb-1">
                <div className={`text-[10px] font-semibold uppercase tracking-wide ${s.titre}`}>
                  {s.label}
                </div>
                <div className="text-[10px] text-gray-500">
                  {total} matière{total > 1 ? 's' : ''}
                </div>
              </div>
              {blocs.length === 0 ? (
                <div className="text-[11px] text-gray-500 italic px-1 py-0.5">Aucune copie associée</div>
              ) : (
                <div className="space-y-1.5">
                  {blocs.map((b: any) => (
                    <div key={b.semestre_global} className="flex items-start gap-2">
                      <span className="font-mono text-[10px] text-gray-500 dark:text-gray-400 font-semibold pt-0.5 w-7 flex-shrink-0">
                        S{b.semestre_global}
                      </span>
                      <div className="flex-1 flex flex-wrap gap-1">
                        {b.matieres.map((m: any) => (
                          <span key={m.matiere_id}
                            className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] border ${s.chipBg} ${s.chipText}`}
                            title={m.nom}>
                            {m.code || m.nom.slice(0, 18)}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function NotesBloc({ devoirs, examens }: {
  devoirs: Record<string, any[]>;
  examens: Record<string, any[]>;
}) {
  const [semestre, setSemestre] = useState<'S1' | 'S2'>('S1');
  const hasS1 = (devoirs.S1?.length || 0) + (examens.S1_NORMAL?.length || 0) + (examens.S1_RATTRAPAGE?.length || 0) > 0;
  const hasS2 = (devoirs.S2?.length || 0) + (examens.S2_NORMAL?.length || 0) + (examens.S2_RATTRAPAGE?.length || 0) > 0;

  // Sélection auto du semestre qui a des données
  React.useEffect(() => {
    if (semestre === 'S1' && !hasS1 && hasS2) setSemestre('S2');
  }, [hasS1, hasS2, semestre]);

  const devSel = devoirs[semestre] || [];
  const examNorm = examens[`${semestre}_NORMAL`] || [];
  const examRatt = examens[`${semestre}_RATTRAPAGE`] || [];

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <h3 className="text-xs font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-1.5">
          <BookOpen size={13} /> Notes — devoirs &amp; examens
        </h3>
        <div className="inline-flex bg-gray-100 dark:bg-gray-700 rounded p-0.5">
          <button onClick={() => setSemestre('S1')}
            className={`px-2.5 py-1 text-[11px] font-medium rounded transition-colors ${
              semestre === 'S1'
                ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}>
            Semestre 1 {!hasS1 && <span className="opacity-60">(vide)</span>}
          </button>
          <button onClick={() => setSemestre('S2')}
            className={`px-2.5 py-1 text-[11px] font-medium rounded transition-colors ${
              semestre === 'S2'
                ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}>
            Semestre 2 {!hasS2 && <span className="opacity-60">(vide)</span>}
          </button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <NotesTable titre="Devoirs" couleur="text-gray-600 dark:text-gray-400"
          colonnes={['Matière', 'D1', 'D2', 'Moy']}
          rows={devSel.map((n) => [
            n.matiere || n.code,
            n.absent1 ? 'Abs' : (n.note1 ?? '—'),
            n.absent2 ? 'Abs' : (n.note2 ?? '—'),
            n.moyenne ?? '—',
          ])} />
        <NotesTable titre="Examens — Ordinaire" couleur="text-blue-600 dark:text-blue-400"
          colonnes={['Matière', 'Note', 'Statut']}
          rows={examNorm.map((n) => [
            n.matiere || n.code,
            n.absent ? 'Abs' : (n.note ?? '—'),
            n.verrouille ? 'Verr.' : 'En saisie',
          ])} />
        <NotesTable titre="Examens — Rattrapage" couleur="text-purple-600 dark:text-purple-400"
          colonnes={['Matière', 'Note', 'Statut']}
          rows={examRatt.map((n) => [
            n.matiere || n.code,
            n.absent ? 'Abs' : (n.note ?? '—'),
            n.verrouille ? 'Verr.' : 'En saisie',
          ])} />
      </div>
    </div>
  );
}

function NotesTable({ titre, couleur, colonnes, rows }: {
  titre: string; couleur: string;
  colonnes: string[]; rows: Array<Array<string | null>>;
}) {
  return (
    <div>
      <div className={`text-[10px] font-semibold uppercase tracking-wide mb-1 ${couleur}`}>
        {titre} <span className="text-gray-400 normal-case font-normal">({rows.length})</span>
      </div>
      {rows.length === 0 ? (
        <div className="text-[11px] text-gray-500 italic px-1 py-2">Aucune note</div>
      ) : (
        <div className="max-h-44 overflow-y-auto">
          <table className="w-full text-[10px]">
            <thead className="sticky top-0 bg-gray-50 dark:bg-gray-700/80">
              <tr>
                {colonnes.map((c, i) => (
                  <th key={i} className={`text-left font-semibold text-gray-600 dark:text-gray-300 px-1.5 py-1 ${
                    i > 0 ? 'text-right w-12' : ''
                  }`}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-t border-gray-100 dark:border-gray-700/50">
                  {r.map((v, j) => {
                    const isNum = j > 0;
                    const num = Number(v);
                    const isAbs = v === 'Abs';
                    return (
                      <td key={j} className={`px-1.5 py-0.5 ${isNum ? 'text-right tabular-nums' : 'truncate max-w-[140px]'} ${
                        isAbs ? 'text-red-500 font-semibold' :
                        isNum && !isNaN(num) && num < 10 ? 'text-red-500 font-semibold' :
                        isNum && !isNaN(num) && num >= 10 ? 'text-green-600 dark:text-green-400 font-semibold' :
                        'text-gray-700 dark:text-gray-300'
                      }`}>
                        {v ?? '—'}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Conduite({ cond }: { cond: any }) {
  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
      <h3 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-1.5">
        <Heart size={13} /> Conduite
      </h3>
      <div className="flex items-center justify-around mb-2 pb-2 border-b border-gray-100 dark:border-gray-700">
        <div className="text-center">
          <div className="text-base font-bold text-green-500">{cond.nb_positives}</div>
          <div className="text-[9px] uppercase text-gray-500">Positifs</div>
        </div>
        <div className="text-center">
          <div className="text-base font-bold text-red-500">{cond.nb_negatives}</div>
          <div className="text-[9px] uppercase text-gray-500">Négatifs</div>
        </div>
        <div className="text-center">
          <div className="text-base font-bold text-gray-900 dark:text-white">{cond.score}</div>
          <div className="text-[9px] uppercase text-gray-500">Score</div>
        </div>
      </div>
      {cond.recent.length === 0 ? (
        <div className="text-center text-[11px] text-gray-500 py-3">Aucune entrée</div>
      ) : (
        <div className="space-y-0.5 max-h-32 overflow-y-auto">
          {cond.recent.slice(0, 6).map((c: any) => (
            <div key={c.id} className="text-[10px] py-1 px-1.5 rounded bg-gray-50 dark:bg-gray-700/40 flex items-start gap-1.5">
              <span className={`inline-block w-1.5 h-1.5 rounded-full mt-1 flex-shrink-0 ${
                (c.poids || 0) > 0 ? 'bg-green-500' : (c.poids || 0) < 0 ? 'bg-red-500' : 'bg-gray-400'
              }`} />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-gray-900 dark:text-white truncate">{c.type}</div>
                <div className="text-[9px] text-gray-500">
                  {c.date && new Date(c.date).toLocaleDateString('fr-FR')} · {c.poids > 0 ? '+' : ''}{c.poids}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
